import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, ArrowRight, ShieldCheck } from 'lucide-react';
import CinescopeLogo from './CinescopeLogo';
import NavbarSearch from './NavbarSearch';
import { apiClient } from '@/shared/api/client';

/**
 * Premium Floating Card Mega-Menu Header for Cinescope India.
 * Implements a modern floating glass-card navbar container with high-end 
 * dual-pane mega-menu cards for Brands and Products, complete with 
 * micro-animations and clean visual alignment.
 */
export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll state
  const [isScrolled, setIsScrolled] = useState(false);
  // Dropdown & Mobile states
  const [isBrandsOpen, setIsBrandsOpen] = useState(false);
  const brandsTimerRef = useRef(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileBrandsOpen, setIsMobileBrandsOpen] = useState(false);
  const [dynamicBrands, setDynamicBrands] = useState([]);

  // Monitor scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (location.pathname !== '/') {
        setIsScrolled(true);
      } else {
        setIsScrolled(window.scrollY > 80);
      }
    };
    
    // Run initially
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Close mobile drawer on route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Fetch dynamic brand list from backend API
  useEffect(() => {
    apiClient.get('/api/v1/products/brands?format=objects&status=publish')
      .then(res => {
        if (res.data) {
          const items = Array.isArray(res.data.items) ? res.data.items : (Array.isArray(res.data) ? res.data : []);
          if (items.length > 0) {
            setDynamicBrands(items);
          }
        }
      })
      .catch(err => {
        console.warn("Failed to fetch navbar dynamic brands from API:", err);
      });
  }, []);

  // Handle smooth scroll resets
  const handleAnchorClick = (anchorId) => {
    if (location.pathname === '/') {
      const el = document.getElementById(anchorId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/', { state: { scrollTo: anchorId } });
    }
  };

  const handleBrandSelect = (brandItem) => {
    let slug = '';
    if (typeof brandItem === 'object' && brandItem.slug) {
      slug = brandItem.slug;
    } else {
      const name = typeof brandItem === 'object' ? brandItem.name : brandItem;
      let cleanName = name;
      if (name.includes('Steadicam')) {
        cleanName = 'Steadicam';
      }
      slug = cleanName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/-+$/, '')
        .replace(/^-+/, '');
    }
    navigate(`/shop/brand/${slug}`);
    setIsBrandsOpen(false);
  };

  const staticBrandsList = [
    { name: 'Atlas Lens Co.', desc: 'Anamorphic Optics', slug: 'atlas-lens-co' },
    { name: 'Betz-Tools GmbH', desc: 'Rig Stabilization', slug: 'betz-tools-gmbh' },
    { name: 'BLKBIRD', desc: 'Custom Cages', slug: 'blkbird' },
    { name: 'Cinema Devices', desc: 'Body Support', slug: 'cinema-devices' },
    { name: 'Dynacore', desc: 'Li-ion Batteries', slug: 'dynacore' },
    { name: 'Eartec', desc: 'Crew Intercoms', slug: 'eartec' },
    { name: 'Egripment', desc: 'Dolly & Cranes', slug: 'egripment' },
    { name: 'Feelworld', desc: 'On-Camera Monitors', slug: 'feelworld' },
    { name: 'FloatCam', desc: 'Linear Sliders', slug: 'floatcam' },
    { name: 'Flowcine', desc: 'Gimbal Arm Postures', slug: 'flowcine' },
    { name: 'Freefly Systems', desc: 'MōVI Gimbals', slug: 'freefly-systems' },
    { name: 'HD Air Studio', desc: 'Gimbal Designs', slug: 'hd-air-studio' },
    { name: 'iFootage', desc: 'Monopods & Jibs', slug: 'ifootage' },
    { name: 'MotoCrane', desc: 'Tracking Vehicles', slug: 'motocrane' },
    { name: 'NB Stabilizer', desc: 'Heavy Stabilizers', slug: 'nb-stabilizer' },
    { name: 'Panther', desc: 'Hydraulic Dollies', slug: 'panther' },
    { name: 'proton', desc: 'Compact Cameras', slug: 'proton' },
    { name: 'SmallHD', desc: 'Field Displays', slug: 'smallhd' },
    { name: 'SmartSystem', desc: 'Motorized Sliders', slug: 'smartsystem' },
    { name: 'Tilta', desc: 'Rigging Cages', slug: 'tilta' },
    { name: 'Steadicam – The Tiffen Company', desc: 'Mechanical Arms', slug: 'steadicam' },
    { name: 'Transvideo', desc: 'Wireless Links', slug: 'transvideo' },
    { name: 'Walter Klassen FX', desc: 'Body Harnesses', slug: 'walter-klassen-fx' },
    { name: 'Zacuto', desc: 'EVFs & Mounts', slug: 'zacuto' }
  ];

  const brandsList = dynamicBrands.length > 0
    ? dynamicBrands.map(b => ({
        name: b.name,
        slug: b.slug,
        desc: b.subheading || b.description || 'Cinema Production Gear'
      }))
    : staticBrandsList;

  const handleBrandsMouseEnter = () => {
    if (brandsTimerRef.current) {
      clearTimeout(brandsTimerRef.current);
      brandsTimerRef.current = null;
    }
    setIsBrandsOpen(true);
  };

  const handleBrandsMouseLeave = () => {
    if (brandsTimerRef.current) {
      clearTimeout(brandsTimerRef.current);
    }
    brandsTimerRef.current = setTimeout(() => {
      setIsBrandsOpen(false);
    }, 220);
  };

  return (
    <div className="fixed top-0 left-0 w-full z-50 px-4 md:px-6 pt-2 md:pt-5 pointer-events-none">
      
      {/* Main Floating Card Container */}
      <nav className={`relative mx-auto max-w-7xl flex items-center justify-between px-4 md:px-6 py-2 md:py-3.5 pointer-events-auto transition-all duration-300 rounded-2xl ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-lg border border-zinc-200/80 shadow-[0_20px_50px_rgba(0,0,0,0.05)] text-zinc-700' 
          : 'bg-black/25 backdrop-blur-md border border-white/10 shadow-none text-white'
      }`}>
        
        {/* Left Section: Logo */}
        <Link 
          to="/" 
          className="flex items-center cursor-pointer select-none"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <CinescopeLogo showSubtitle={false} textColor={isScrolled ? 'text-zinc-900' : 'text-white'} />
        </Link>

        {/* Center Section: Navigation Links */}
        <div className="hidden xl:flex items-center gap-x-1.5">
          
          {/* Brands Mega Dropdown Trigger */}
          <div 
            className="relative"
            onMouseEnter={handleBrandsMouseEnter}
            onMouseLeave={handleBrandsMouseLeave}
          >
            <button 
              className={`flex items-center gap-1 px-4 py-2.5 rounded-xl font-sans text-xs font-bold tracking-wider uppercase transition-all duration-200 focus:outline-none cursor-pointer ${
                isBrandsOpen 
                  ? (isScrolled ? 'bg-[#F4F7F9] text-[#793bfb]' : 'bg-white/15 text-white') 
                  : (isScrolled ? 'text-zinc-700 hover:bg-[#F4F7F9]/75 hover:text-[#793bfb]' : 'text-white/80 hover:bg-white/10 hover:text-white')
              }`}
            >
              Brands
              <ChevronDown className="w-3.5 h-3.5 opacity-60" />
            </button>
          </div>

          {/* Store Link */}
          <Link 
            to="/shop"
            className={`px-4 py-2.5 rounded-xl font-sans text-xs font-bold tracking-wider uppercase transition-all focus:outline-none ${
              isScrolled 
                ? 'text-zinc-700 hover:bg-[#F4F7F9]/75 hover:text-[#793bfb]' 
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            Store
          </Link>

          {/* About Us Link */}
          <button 
            onClick={() => handleAnchorClick('about-us-anchor')}
            className={`px-4 py-2.5 rounded-xl font-sans text-xs font-bold tracking-wider uppercase transition-all focus:outline-none ${
              isScrolled 
                ? 'text-zinc-700 hover:bg-[#F4F7F9]/75 hover:text-[#793bfb]' 
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            About Us
          </button>

          {/* Rig Planner Link - Temporarily Hidden */}
          {/* <button 
            onClick={() => handleAnchorClick('rig-labs-anchor')}
            className={`px-4 py-2.5 rounded-xl font-sans text-xs font-bold tracking-wider uppercase transition-all focus:outline-none ${
              isScrolled 
                ? 'text-zinc-700 hover:bg-[#F4F7F9]/75 hover:text-[#793bfb]' 
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            Rig Builder
          </button> */}

          {/* Knowledge Center Link */}
          <Link 
            to="/knowledge"
            className={`px-4 py-2.5 rounded-xl font-sans text-xs font-bold tracking-wider uppercase transition-all ${
              isScrolled 
                ? 'text-zinc-700 hover:bg-[#F4F7F9]/75 hover:text-[#793bfb]' 
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            Knowledge Center
          </Link>

          {/* Repair Service Link */}
          <Link 
            to="/shop/all?tab=repair"
            className={`px-4 py-2.5 rounded-xl font-sans text-xs font-bold tracking-wider uppercase transition-all ${
              isScrolled 
                ? 'text-zinc-700 hover:bg-[#F4F7F9]/75 hover:text-[#793bfb]' 
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            Repair Service
          </Link>

          {/* Scheduler Link - Temporarily Commented */}
          {/* <Link 
            to="/scheduler"
            className={`px-4 py-2.5 rounded-xl font-sans text-xs font-bold tracking-wider uppercase transition-all focus:outline-none ${
              isScrolled 
                ? 'text-zinc-700 hover:bg-[#F4F7F9]/75 hover:text-[#793bfb]' 
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            Scheduler
          </Link> */}
        </div>

        {/* Right Section: Search & Call to action buttons */}
        <div className="hidden xl:flex items-center gap-3">
          <NavbarSearch isScrolled={isScrolled} />

          <Link 
            to="/contact"
            className="bg-[#793bfb] hover:bg-[#8d52ff] text-white px-5 py-2.5 rounded-xl text-[11px] font-sans font-bold uppercase tracking-wider transition-all shadow-[0_4px_14px_rgba(121,59,251,0.25)] hover:shadow-[0_6px_20px_rgba(121,59,251,0.35)] hover:scale-[1.02] shrink-0"
          >
            Specialist Advice
          </Link>
        </div>

        {/* Mobile menu trigger */}
        <div className="xl:hidden flex items-center gap-1.5">
          <NavbarSearch isScrolled={isScrolled} />

          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`p-2.5 border rounded-xl transition-colors focus:outline-none active:scale-95 ${
              isScrolled 
                ? 'bg-[#F4F7F9]/60 hover:bg-[#F4F7F9] border-zinc-200 text-zinc-800' 
                : 'bg-white/10 hover:bg-white/20 border-white/10 text-white'
            }`}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Brands Mega Dropdown Popup (Centered relative to the floating Navbar container) */}
        {isBrandsOpen && (
          <div 
            className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50 animate-fadeIn"
            onMouseEnter={handleBrandsMouseEnter}
            onMouseLeave={handleBrandsMouseLeave}
          >
            <div 
              className="w-[880px] max-w-[calc(100vw-32px)] bg-white border border-zinc-200/90 rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.18)] overflow-hidden grid grid-cols-5 text-zinc-700 font-sans"
            >
              {/* Left Column: Brand list (3/5 width) */}
              <div className="col-span-3 p-6 space-y-2">
                <div className="text-[10px] font-mono text-zinc-400 font-extrabold uppercase tracking-widest border-b border-zinc-200/40 pb-2 mb-2">
                  Authorized Brand Catalogs
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 max-h-[360px] overflow-y-auto pr-2">
                  {brandsList.map((brand) => (
                    <button
                      key={brand.slug || brand.name}
                      onClick={() => handleBrandSelect(brand)}
                      className="flex flex-col text-left p-2.5 rounded-xl hover:bg-[#F4F7F9] transition-all group/item cursor-pointer"
                    >
                      <span className="text-[11px] font-display font-extrabold text-zinc-900 uppercase tracking-wide group-hover/item:text-[#793bfb] transition-colors flex items-center gap-1">
                        {brand.name}
                        <ArrowRight className="w-3 h-3 opacity-0 group-hover/item:opacity-100 transition-all -translate-x-1 group-hover/item:translate-x-0" />
                      </span>
                      <span className="text-[9px] text-zinc-500 font-sans mt-0.5 line-clamp-1">
                        {brand.desc}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Right Column: Featured visual card (2/5 width) */}
              <div className="col-span-2 bg-[#F4F7F9] p-6 border-l border-zinc-200/60 flex flex-col justify-between">
                <div className="space-y-4">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-zinc-200 rounded-full text-[9px] font-mono text-olive font-extrabold uppercase tracking-wider">
                    <ShieldCheck className="w-3.5 h-3.5 text-sage" /> Verified Partner
                  </span>
                  <h4 className="font-display font-black text-zinc-950 text-xl uppercase tracking-wide leading-tight">
                    Cinema Grade Equipment
                  </h4>
                  <p className="text-[10.5px] text-zinc-650 leading-relaxed font-sans">
                    Cinescope is the exclusive national importer and service partner for certified cinema brands in India. Full warranty support included.
                  </p>
                </div>

                <Link
                  to="/shop"
                  onClick={() => setIsBrandsOpen(false)}
                  className="mt-6 inline-flex items-center justify-center gap-1.5 w-full bg-zinc-900 hover:bg-zinc-850 text-white font-mono text-[9px] font-bold py-3 rounded-xl uppercase tracking-wider transition-colors shadow-sm cursor-pointer"
                >
                  Browse Store Catalog <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </div>
        )}

      </nav>

      {/* Mobile drawer container */}
      {isMobileMenuOpen && (
        <div 
          className="mx-auto max-w-7xl mt-2 bg-white border border-zinc-200 rounded-2xl shadow-xl overflow-hidden xl:hidden pointer-events-auto animate-fadeIn"
        >
          <div className="p-6 space-y-5 flex flex-col max-h-[calc(100vh-140px)] overflow-y-auto">
            

            {/* Mobile Brands Accordion */}
            <div className="space-y-1">
              <button 
                onClick={() => setIsMobileBrandsOpen(!isMobileBrandsOpen)}
                className="flex justify-between items-center w-full text-left font-sans text-xs font-bold uppercase tracking-wider py-2 text-zinc-850"
              >
                <span>Brands</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isMobileBrandsOpen ? 'rotate-180 text-[#793bfb]' : 'text-zinc-500'}`} />
              </button>
              
              {isMobileBrandsOpen && (
                <div className="pl-3 border-l border-zinc-200 space-y-1 mt-1 py-1">
                  {brandsList.map((brand) => (
                    <button
                      key={brand.slug || brand.name}
                      onClick={() => {
                        handleBrandSelect(brand);
                        setIsMobileMenuOpen(false);
                      }}
                      className="block w-full text-left py-2 px-2.5 rounded-lg hover:bg-[#F4F7F9] text-xs font-semibold text-zinc-700 uppercase tracking-wider"
                    >
                      {brand.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link 
              to="/shop"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left font-sans text-xs font-bold uppercase tracking-wider text-zinc-800 py-2 border-b border-zinc-100"
            >
              Store
            </Link>

            {/* Mobile links */}
            <button 
              onClick={() => {
                handleAnchorClick('about-us-anchor');
                setIsMobileMenuOpen(false);
              }}
              className="block w-full text-left font-sans text-xs font-bold uppercase tracking-wider text-zinc-800 py-2 border-b border-zinc-100"
            >
              About Us
            </button>

            <Link 
              to="/knowledge"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left font-sans text-xs font-bold uppercase tracking-wider text-zinc-800 py-2 border-b border-zinc-100"
            >
              Knowledge Center
            </Link>

            {/* Mobile Repair Service Link */}
            <Link 
              to="/shop/all?tab=repair"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left font-sans text-xs font-bold uppercase tracking-wider text-zinc-800 py-2 border-b border-zinc-100"
            >
              Repair Service
            </Link>

            {/* Mobile Scheduler Link - Temporarily Commented */}
            {/* <Link 
              to="/scheduler"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left font-sans text-xs font-bold uppercase tracking-wider text-zinc-800 py-2 border-b border-zinc-100"
            >
              Scheduler
            </Link> */}

            {/* Mobile Action triggers */}
            <div className="pt-4">
              <button 
                onClick={() => {
                  handleAnchorClick('contact-anchor');
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-center py-3 rounded-xl bg-[#793bfb] hover:bg-[#8d52ff] text-white text-xs font-sans font-bold uppercase tracking-wider active:scale-95 shadow-md"
              >
                Specialist Advice
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
